import { type SelectedJurisdictions } from '../../types';

export interface SearchFilters {
  jurisdictions: SelectedJurisdictions;
  textFilters: Record<string, string>;
  moreFilters: Record<string, string>;
}

export type ApiFilters = Record<string, string | number | boolean>;

export const CASES_TEXT_FILTER_MAPPINGS: Record<string, string> = {
  caseName: 'case_name',
  docketNumber: 'docket_number',
  judge: 'judge',
};

export const CASES_MORE_FILTER_MAPPINGS: Record<string, string> = {
  attorneyName: 'atty_name',
  partyName: 'party_name',
  orderBy: 'order_by',
};

export const OPINIONS_TEXT_FILTER_MAPPINGS: Record<string, string> = {
  caseName: 'case_name',
  docketNumber: 'docket_number',
  judge: 'judge',
  citation: 'citation',
};

export const OPINIONS_MORE_FILTER_MAPPINGS: Record<string, string> = {
  orderBy: 'order_by',
};

export const ORAL_ARGUMENTS_TEXT_FILTER_MAPPINGS: Record<string, string> = {
  caseName: 'case_name',
  docketNumber: 'docket_number',
  judge: 'judge',
};

export const ORAL_ARGUMENTS_MORE_FILTER_MAPPINGS: Record<string, string> = {
  orderBy: 'order_by',
};

export const JUDGE_SEARCH_TEXT_FILTER_MAPPINGS: Record<string, string> = {
  name: 'name',
  dobCity: 'dob_city',
  dobState: 'dob_state',
  bornAfter: 'born_after',
  bornBefore: 'born_before',
  appointedBy: 'appointed_by',
  school: 'school',
};

export const JUDGE_SEARCH_MORE_FILTER_MAPPINGS: Record<string, string> = {
  orderBy: 'order_by',
  selectionMethod: 'selection_method',
  politicalAffiliation: 'political_affiliation',
};

const processFilters = (
  result: ApiFilters,
  filterObj: Record<string, string>,
  mappings: Record<string, string>
): void => {
  Object.entries(filterObj).forEach(([key, value]) => {
    if (value?.trim()) {
      result[mappings[key] || key] = value.trim();
    }
  });
};

export const normalizeCasesFiltersToApiParams = (
  filters: SearchFilters
): ApiFilters => {
  const result: ApiFilters = {};

  processFilters(result, filters.textFilters, CASES_TEXT_FILTER_MAPPINGS);
  processFilters(result, filters.moreFilters, CASES_MORE_FILTER_MAPPINGS);

  if (filters.jurisdictions) {
    result.jurisdictions = filters.jurisdictions as unknown as boolean;
  }

  return result;
};

export const normalizeOpinionsFiltersToApiParams = (
  filters: SearchFilters
): ApiFilters => {
  const result: ApiFilters = {};

  processFilters(result, filters.textFilters, OPINIONS_TEXT_FILTER_MAPPINGS);
  processFilters(result, filters.moreFilters, OPINIONS_MORE_FILTER_MAPPINGS);

  if (filters.jurisdictions) {
    result.jurisdictions = filters.jurisdictions as unknown as boolean;
  }

  return result;
};

export const normalizeOralArgumentsFiltersToApiParams = (
  filters: SearchFilters
): ApiFilters => {
  const result: ApiFilters = {};

  processFilters(
    result,
    filters.textFilters,
    ORAL_ARGUMENTS_TEXT_FILTER_MAPPINGS
  );
  processFilters(
    result,
    filters.moreFilters,
    ORAL_ARGUMENTS_MORE_FILTER_MAPPINGS
  );

  if (filters.jurisdictions) {
    result.jurisdictions = filters.jurisdictions as unknown as boolean;
  }

  return result;
};

export const normalizeJudgeSearchFiltersToApiParams = (
  filters: SearchFilters
): ApiFilters => {
  const result: ApiFilters = {};

  processFilters(
    result,
    filters.textFilters,
    JUDGE_SEARCH_TEXT_FILTER_MAPPINGS
  );
  processFilters(
    result,
    filters.moreFilters,
    JUDGE_SEARCH_MORE_FILTER_MAPPINGS
  );

  if (filters.jurisdictions) {
    result.jurisdictions = filters.jurisdictions as unknown as boolean;
  }

  return result;
};

export const normalizeV2FiltersToApiParams = (
  filters: SearchFilters,
  searchType: 'cases' | 'opinions'
): ApiFilters => {
  if (searchType === 'cases') {
    return normalizeCasesFiltersToApiParams(filters);
  }
  return normalizeOpinionsFiltersToApiParams(filters);
};

export const prepareQueryForCacheKey = (
  query: string,
  exactMatch: boolean
): string => {
  const trimmedQuery = query.trim() || 'empty-query';

  if (trimmedQuery === 'empty-query') {
    return trimmedQuery;
  }

  const isAlreadyQuoted =
    trimmedQuery.startsWith('"') &&
    trimmedQuery.endsWith('"') &&
    trimmedQuery.length >= 2;

  if (exactMatch) {
    return isAlreadyQuoted ? trimmedQuery : `"${trimmedQuery}"`;
  }
  return isAlreadyQuoted ? trimmedQuery.slice(1, -1) : trimmedQuery;
};
